import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { style } from 'typestyle';
import moment from 'moment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlusIcon from 'mdi-react/PlusIcon';
import BullhornOutlineIcon from 'mdi-react/BullhornOutlineIcon';
import { IsMobileContext } from '../../../core/context/is-mobile';
import * as styles from '../offices.styles';
import * as communicationStyles from '../office-communication.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';

import type Announcement from '../models/announcement';
import { Center } from '../../../core/components/center/center.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import StyleConstants from '../../../core/styles.constants';
import AnnouncementCard from '../cards/office-announcement-card.component';
import MessageGroup from '../cards/message-group.component';
import { announcementStatus } from '../message-status';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import notify from '../../../core/components/notify/notify.component';
import { createAnnouncement, deleteAnnouncement, updateAnnouncement } from '../services/announcement.services';
import CreateAnnouncementModal from '../modals/create-announcement-modal.component';
import AppConstants from '../../../core/app.constants';

interface Props {
  announcements: Announcement[];
  readonly officeId: string | undefined;
  announcementsError: number | undefined;
}

const OfficeAnnouncementsGroup = ({
  announcements: initialAnnouncements,
  officeId,
  announcementsError: initialAnnouncementsError,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const isMobile = useContext(IsMobileContext);
  const [showCreateAnnouncementModal, setShowCreateAnnouncementModal] = useState(false);

  const [announcements, setAnnouncements] = useState(initialAnnouncements);
  const sortedAnnouncements = announcements?.sort((a, b) => new Date(a.endDate ?? 0).getTime() - new Date(b.endDate ?? 0).getTime()) ?? [];
  const handleShowCreateModal = (): void => {
    setShowCreateAnnouncementModal(true);
  };

  const keycloak = useKeycloak();

  const [isLoading, setIsLoading] = useState(true);

  const [announcementsError] = useState<number | undefined>(initialAnnouncementsError);

  const handleClose = useCallback(() => {
    setShowCreateAnnouncementModal(false);
  }, []);

  const announcementIsValid = (announcementToValidate: Announcement): boolean => {
    if (stringIsNullOrWhitespace(announcementToValidate.message)) {
      notify(t('announcement-message-empty-message'), false);
      setIsLoading(false);
      return false;
    }

    if (announcementToValidate.startDate == null) {
      notify(t('startdate-empty-message'), false);
      setIsLoading(false);
      return false;
    }

    if (announcementToValidate.endDate == null) {
      notify(t('enddate-empty-message'), false);
      setIsLoading(false);
      return false;
    }

    if (announcementToValidate.endDate < announcementToValidate.startDate) {
      notify(t('invalid-date-range-message'), false);
      setIsLoading(false);
      return false;
    }

    if (announcementToValidate.endDate < moment().startOf('day').toDate()) {
      notify(t('past-date-message'), false);
      setIsLoading(false);
      return false;
    }

    return true;
  };

  const handleCreateAnnouncement = useCallback(
    async (announcement: Announcement): Promise<void> => {
      if (stringIsNullOrWhitespace(announcement.officeId)) return;

      const response = await createAnnouncement(announcement, keycloak);

      if (!response) {
        notify(t('office-announcement-creation-error-message'), false);
        setIsLoading(false);
        return;
      }

      const announcementsCopy = [...announcements];
      announcementsCopy.push(response);

      setAnnouncements(announcementsCopy);

      notify(t('office-announcement-creation-success-message'), true);

      handleClose();
    },
    [announcements, keycloak, officeId],
  );

  const shouldUpdateAnnouncement = async (originalAnnouncement: Announcement, updatedAnnouncement: Announcement): Promise<boolean> => {
    if (!officeId) return false;

    const response = await updateAnnouncement(originalAnnouncement, updatedAnnouncement, keycloak);

    if (!response) {
      notify(t('office-announcement-edit-error-message'), false);
      setIsLoading(false);
      return false;
    }

    const filteredAnnouncements = announcements?.filter((announcement) => announcement.id !== response.id);
    filteredAnnouncements?.push(response);

    setAnnouncements(filteredAnnouncements);

    notify(t('office-announcement-edit-success-message'), true);

    return true;
  };

  const shouldDeleteAnnouncement = async (announcementToDelete: Announcement): Promise<boolean> => {
    const success = await deleteAnnouncement(announcementToDelete, keycloak);
    if (success) {
      const remainingAnnouncements = announcements?.filter((announcement) => announcement.id !== announcementToDelete.id);
      setAnnouncements(remainingAnnouncements);
      return true;
    }
    notify(t('delete-announcement-error-message'), false);
    return false;
  };

  if (isLoading) {
    setIsLoading(false);
  }

  const getErrorMessageText = useCallback(
    (errorToCheck: number) => {
      switch (errorToCheck) {
        case 401:
          return t('unauthorized-error');
        default:
          return t('unknown');
      }
    },
    [t],
  );

  const renderCard = (announcement: Announcement): React.JSX.Element => (
    <AnnouncementCard
      key={announcement.id}
      initialAnnouncement={announcement}
      handleUpdateAnnouncement={shouldUpdateAnnouncement}
      announcementIsValid={announcementIsValid}
      onDelete={shouldDeleteAnnouncement}
    />
  );

  // Announcements are shown by what they are doing right now rather than as one flat list, so the
  // banners currently live in the app sit at the top and finished ones fold away.
  const inStatus = (status: ReturnType<typeof announcementStatus>): Announcement[] =>
    sortedAnnouncements.filter((announcement) => announcementStatus(announcement.startDate, announcement.endDate) === status);

  return (
    <div className={style(communicationStyles.card)}>
      <div className={style(communicationStyles.sectionHead(isMobile))}>
        <div className={style(communicationStyles.sectionHeadLeft)}>
          <div className={style(communicationStyles.tile)}>
            <BullhornOutlineIcon size={20} />
          </div>
          <div>
            <h2 className={style(communicationStyles.sectionTitle)}>
              {t('announcements')}
              <span className={style(communicationStyles.countPill)}>{sortedAnnouncements.length}</span>
              <InfoOutlinedIcon
                data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
                data-tooltip-content={t('announcements-info-message').toString()}
                sx={styles.tooltipIconSX}
              />
            </h2>
            <p className={style(communicationStyles.lede)}>{t('announcements-card-subtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          className={`${style(surfaceStyles.primaryButton)} button_create_announcement`}
          onClick={handleShowCreateModal}
          disabled={isLoading}
        >
          <PlusIcon size={16} />
          {t('create-announcement')}
        </button>
      </div>
      <div className={style(communicationStyles.sectionBody)}>
        <div className={style(communicationStyles.bodyDivider)} />
        {((): React.JSX.Element => {
          if (isLoading) {
            return (
              <Center>
                <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
              </Center>
            );
          }

          if (announcementsError) {
            return (
              <span className={style(communicationStyles.errorMessage)}>
                {t('office-announcement-get-error')}
                {': '}
                {getErrorMessageText(announcementsError)}
              </span>
            );
          }

          if (sortedAnnouncements === undefined || sortedAnnouncements.length === 0) {
            return <span className={style(communicationStyles.emptyMessage)}>{t('no-announcements-available-message')}</span>;
          }

          const active = inStatus('active');
          const scheduled = inStatus('scheduled');
          const past = inStatus('past');

          return (
            <div>
              <MessageGroup label={t('message-status-live') ?? ''} count={active.length}>
                {active.map(renderCard)}
              </MessageGroup>
              <MessageGroup label={t('message-status-scheduled') ?? ''} count={scheduled.length}>
                {scheduled.map(renderCard)}
              </MessageGroup>
              <MessageGroup label={t('message-status-finished') ?? ''} count={past.length} collapsible>
                {past.map(renderCard)}
              </MessageGroup>
            </div>
          );
        })()}
      </div>
      <CreateAnnouncementModal
        showModal={showCreateAnnouncementModal}
        handleClose={handleClose}
        handleCreate={(announcement): void => {
          handleCreateAnnouncement(announcement);
        }}
        isAnnouncementValid={announcementIsValid}
        officeId={officeId ?? ''}
      />
    </div>
  );
};

export default OfficeAnnouncementsGroup;
